import { hasQrCodeLink, PostQrCode } from "@package/ui-apollo-components";
import { PostQrCodeLocationType } from "@package/ui-gql/index-data-access";
import { useSelectorWall } from "@package/ui-redux";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import { type PropsWithChildren } from "react";

type FlyoutQrCodeProps = {
  screen: SliceWallScreenState;
};

function FlyoutQrCodePaddedContainer({
  children,
  text,
}: PropsWithChildren<{ text?: string | null }>) {
  return (
    <div className="p-4 rounded-2xl bg-black/25">
      <div
        className="p-8 rounded-xl bg-white"
        style={{ inlineSize: "min-content" }}
      >
        {children}
        <div className="mt-8 font-bold text-4xl text-black text-center">
          {text ?? "SCAN HERE LEARN MORE"}
        </div>
      </div>
    </div>
  );
}

function FlyoutQrCodePositionedContainer({
  children,
  screen,
}: PropsWithChildren<FlyoutQrCodeProps>) {
  const type =
    screen.flyout?.post?.qrCodeLocation?.type ?? PostQrCodeLocationType.Bottom;

  if (type === PostQrCodeLocationType.Top) {
    return (
      <div
        className="absolute top-1/3 left-1/2"
        style={{ transform: "translate(-50%, -33.3%)" }}
      >
        {children}
      </div>
    );
  }

  if (type === PostQrCodeLocationType.Bottom) {
    return (
      <div
        className="absolute bottom-1/3 left-1/2"
        style={{ transform: "translate(-50%, 33.3%)" }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className="absolute"
      style={{
        left: screen.flyout?.post?.qrCodeLocation?.x,
        top: screen.flyout?.post?.qrCodeLocation?.y,
        width: screen.flyout?.post?.qrCodeLocation?.width,
        height: screen.flyout?.post?.qrCodeLocation?.height,
      }}
    >
      {children}
    </div>
  );
}

export function FlyoutQrCode({ screen }: FlyoutQrCodeProps) {
  const location = useSelectorWall((state) => state.wall.location);
  const profile = useSelectorWall((state) => state.wall.profile);
  const services = useSelectorWall((state) => state.wall.services);
  const serviceFlyout = services?.find(
    (s) => s.id === screen?.flyout?.post?.flyoutType,
  );
  const isEmbedded =
    !!serviceFlyout?.options?.cms?.post?.qrCodeEmbeddedInFlyout;
  const qrCodeText = screen?.flyout?.post?.qrCodeText;

  if (isEmbedded || !hasQrCodeLink(location, profile, screen?.flyout?.post)) {
    return null;
  }

  return (
    <FlyoutQrCodePositionedContainer screen={screen}>
      <FlyoutQrCodePaddedContainer text={qrCodeText}>
        <PostQrCode
          location={location}
          profile={profile}
          post={screen?.flyout?.post}
        />
      </FlyoutQrCodePaddedContainer>
    </FlyoutQrCodePositionedContainer>
  );
}
